import {
  fnv1a64,
  stableStringify,
} from '@asset-viz/reporting/reportHeatmap/utils'
import {
  DrilldownExplorationConfiguration,
  DrilldownPanelState,
  DrilldownViewState,
} from 'features/drilldown/types'

export const EXPLORATION_SNAPSHOT_VERSION = 1

export type ExplorationPanelSnapshot = {
  id: string
  panel_type: string
  panel_data: Record<string, unknown>
}

export type ExplorationSnapshot = {
  version: typeof EXPLORATION_SNAPSHOT_VERSION
  dashboard_id: string
  revision: string
  panels: ReadonlyArray<ExplorationPanelSnapshot>
}

const SENSITIVE_KEY = /token|secret|password|authorization|cookie|credential/i

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const containsSensitiveKey = (value: unknown): boolean => {
  if (Array.isArray(value)) return value.some(containsSensitiveKey)
  if (!isRecord(value)) return false
  return Object.entries(value).some(
    ([key, nested]) => SENSITIVE_KEY.test(key) || containsSensitiveKey(nested)
  )
}

const toJsonRecord = (value: unknown): Record<string, unknown> | undefined => {
  if (!isRecord(value)) return undefined
  try {
    const json = JSON.stringify(value)
    const decoded = json === undefined ? undefined : JSON.parse(json)
    return isRecord(decoded) ? decoded : undefined
  } catch {
    return undefined
  }
}

const mapToEntries = <K, V>(map: ReadonlyMap<K, V> | undefined) =>
  map ? Array.from(map.entries()) : undefined

export const snapshotPanel = (
  panel: DrilldownPanelState
): ExplorationPanelSnapshot | undefined => {
  if (panel.panelType === 'llmPanel' || containsSensitiveKey(panel.panelData)) {
    return undefined
  }
  const panelData = toJsonRecord(panel.panelData)
  return panelData
    ? { id: panel.id, panel_type: panel.panelType, panel_data: panelData }
    : undefined
}

const getRevisionInput = (
  panels: DrilldownViewState,
  configuration: DrilldownExplorationConfiguration
) => ({
  panels: panels
    .filter((panel) => panel.panelType !== 'llmPanel')
    .map((panel) => ({
      id: panel.id,
      panelType: panel.panelType,
      panelData: panel.panelData,
      filters: mapToEntries(panel.filters),
      focusItems: mapToEntries(panel.focusItems),
    })),
  configuration,
})

export const getExplorationRevision = (
  panels: DrilldownViewState,
  configuration: DrilldownExplorationConfiguration
): string => fnv1a64(stableStringify(getRevisionInput(panels, configuration)))

/** Builds a safe snapshot of the current Explorer state. */
export const buildExplorationSnapshot = (
  explorationId: string,
  panels: DrilldownViewState,
  configuration: DrilldownExplorationConfiguration
): ExplorationSnapshot => ({
  version: EXPLORATION_SNAPSHOT_VERSION,
  dashboard_id: explorationId,
  revision: getExplorationRevision(panels, configuration),
  panels: panels.flatMap((panel) => {
    const snapshot = snapshotPanel(panel)
    return snapshot ? [snapshot] : []
  }),
})
